import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, ChevronRight, ChevronLeft } from 'lucide-react';
import Tooltip from './Tooltip';

const CustomDatePicker = ({ value, onChange, t, lang, duration, sprints = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const [orientation, setOrientation] = useState('down'); 
  const triggerRef = useRef(null);
  
  const parseDate = (v) => {
    if (!v) return null;
    const parts = v.split('-').map(Number);
    if (parts.length !== 3) return null;
    return new Date(parts[0], parts[1] - 1, parts[2]);
  };

  const selectedDate = parseDate(value);
  const [viewDate, setViewDate] = useState(selectedDate || new Date());
  
  useEffect(() => { 
    if (selectedDate) {
      setViewDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1)); 
    }
  }, [value]);

  const updateCoords = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const calendarHeight = 380; 
      
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      const newOrientation = (spaceBelow < calendarHeight && spaceAbove > spaceBelow) ? 'up' : 'down';
      
      setOrientation(newOrientation);
      setCoords({ 
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  };

  useLayoutEffect(() => {
    if (isOpen) {
      updateCoords();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('resize', updateCoords);
      window.addEventListener('scroll', updateCoords, true);
    }
    return () => {
      window.removeEventListener('resize', updateCoords);
      window.removeEventListener('scroll', updateCoords, true);
    };
  }, [isOpen]);

  const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const firstDayOfMonth = (y, m) => new Date(y, m, 1).getDay();
  
  const handleDateClick = (day) => {
    const d = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    onChange(`${yr}-${mo}-${da}`);
    setIsOpen(false);
  };

  const weekDays = lang === 'es' ? ['D', 'L', 'M', 'X', 'J', 'V', 'S'] : ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const days = [];
  for (let i = 0; i < firstDayOfMonth(viewDate.getFullYear(), viewDate.getMonth()); i++) days.push(null);
  for (let i = 1; i <= daysInMonth(viewDate.getFullYear(), viewDate.getMonth()); i++) days.push(i);

  const startTime = selectedDate ? new Date(selectedDate).setHours(0,0,0,0) : null;
  const rangeEndDate = selectedDate ? new Date(selectedDate) : null;
  if (rangeEndDate && duration) rangeEndDate.setDate(rangeEndDate.getDate() + parseInt(duration));
  const endTime = rangeEndDate ? rangeEndDate.setHours(0,0,0,0) : null;
  const todayTime = new Date().setHours(0,0,0,0);

  return (
    <div className="relative group">
      <div className="relative">
        <Calendar size={14} className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors z-10 ${isOpen ? 'text-primary' : 'text-slate-500'} pointer-events-none`} />
        <button 
          ref={triggerRef}
          type="button" 
          onClick={() => setIsOpen(!isOpen)} 
          className={`w-full bg-slate-950/40 border rounded-2xl pl-11 pr-5 py-4 text-white text-left text-sm flex items-center justify-between hover:bg-white/10 transition-all shadow-inner ${isOpen ? 'border-primary/50' : 'border-white/5'}`}
        >
          <span className="font-bold">{selectedDate ? selectedDate.toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : t('selectOption')}</span>
          <ChevronRight size={14} className={`text-slate-500 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
        </button>
      </div>

      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <div className="fixed inset-0 z-[999999]">
              <div className="absolute inset-0 bg-transparent" onClick={() => setIsOpen(false)} />
              <motion.div 
                 initial={{ opacity: 0, y: orientation === 'down' ? -20 : 20, scale: 0.95 }} 
                 animate={{ opacity: 1, y: 0, scale: 1 }} 
                 exit={{ opacity: 0, y: orientation === 'down' ? -20 : 20, scale: 0.95 }} 
                 className="fixed w-72 bg-slate-900/95 backdrop-blur-3xl border border-white/10 rounded-[28px] shadow-[0_30px_60px_-12px_rgba(0,0,0,0.8)] p-6"
                 style={{ 
                   top: orientation === 'down' ? coords.bottom + 8 : 'auto',
                   bottom: orientation === 'up' ? (window.innerHeight - coords.top) + 8 : 'auto',
                   left: coords.left,
                 }}
              >
                <div className="flex items-center justify-between mb-5 px-1">
                  <button type="button" onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))} className="p-2 rounded-xl hover:bg-white/5 text-slate-400 border border-white/5 transition-colors"><ChevronLeft size={16} /></button>
                  <span className="text-xs font-black uppercase text-white tracking-[0.1em]">{viewDate.toLocaleString(lang === 'es' ? 'es-ES' : 'en-US', { month: 'long', year: 'numeric' })}</span>
                  <button type="button" onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))} className="p-2 rounded-xl hover:bg-white/5 text-slate-400 border border-white/5 transition-colors"><ChevronRight size={16} /></button>
                </div>
                <div className="grid grid-cols-7 mb-3 text-center">{weekDays.map((d, idx) => <div key={`${d}-${idx}`} className="text-[10px] font-black text-slate-600 uppercase tracking-tighter">{d}</div>)}</div>
                <div className="grid grid-cols-7 gap-1">
                  {days.map((day, idx) => {
                    if (day === null) return <div key={`empty-${idx}`} />;
                    const dayDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
                    const dayTime = dayDate.setHours(0,0,0,0);

                    const isToday = dayTime === todayTime;
                    const isPast = dayTime < todayTime;
                    
                    const occupyingSprint = sprints.find(s => {
                      if (s.status === 'closed' || s.status === 'completed') return false;
                      const sStart = new Date(s.startDate).setHours(0,0,0,0);
                      const sEnd = new Date(s.endDate).setHours(0,0,0,0);
                      return dayTime >= sStart && dayTime <= sEnd;
                    });

                    const overlappingSprint = !occupyingSprint ? sprints.find(s => {
                      if (s.status === 'closed' || s.status === 'completed') return false;
                      const sStart = new Date(s.startDate).setHours(0,0,0,0);
                      const sEnd = new Date(s.endDate).setHours(0,0,0,0);
                      
                      const newStart = dayTime;
                      const newEnd = new Date(dayTime);
                      newEnd.setDate(newEnd.getDate() + (parseInt(duration || 0) - 1));
                      const newEndTime = newEnd.setHours(0,0,0,0);
                      return (newStart <= sEnd && newEndTime >= sStart);
                    }) : null;

                    const isInside = !!occupyingSprint;
                    const isOverlapPotential = !!overlappingSprint;

                    const isSelected = startTime && dayTime === startTime;
                    const isEnd = endTime && dayTime === endTime;
                    const isInRange = startTime && endTime && dayTime > startTime && dayTime < endTime;

                    const dayButton = (
                      <button 
                        key={day} 
                        type="button" 
                        disabled={isInside && !isSelected} 
                        onClick={() => handleDateClick(day)} 
                        className={`group/day h-8 w-8 rounded-[10px] text-[11px] font-bold flex items-center justify-center transition-all relative ${
                          isSelected || isEnd ? 'bg-primary text-white z-10 shadow-lg shadow-primary/20 scale-110' : 
                          isInside ? 'text-rose-500/40 cursor-not-allowed bg-rose-500/5' :
                          isOverlapPotential ? 'text-amber-400 bg-amber-500/5' :
                          isInRange ? 'bg-primary/20 text-primary rounded-none' :
                          isToday ? 'bg-white/10 text-primary ring-1 ring-primary/30' : 
                          isPast ? 'text-slate-500 font-medium hover:bg-white/5' : 
                          'text-slate-200 hover:bg-white/10 hover:scale-110'
                        }`}
                      >
                        {day}
                      </button>
                    );

                    if (isInside && !isSelected) {
                      return (
                        <Tooltip key={day} content={`${t('sprintOccupied') || 'Fecha ocupada'}: ${occupyingSprint.name}`}>
                          {dayButton}
                        </Tooltip>
                      );
                    }

                    if (isOverlapPotential && !isSelected) {
                      return (
                        <Tooltip key={day} content={`${t('sprintOverlapWarning') || 'Solapamiento con'}: ${overlappingSprint.name}`}>
                          {dayButton}
                        </Tooltip>
                      );
                    }

                    return dayButton;
                  })}
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default CustomDatePicker;
